// src/components/whispers/WhisperBanner.tsx
//
// A subtle single-line banner that sits above the chat composer
// when there's an active whisper (a proactive AI nudge the server
// wrote because the user is lingering near a collection-worthy
// item they haven't found yet). Tap → routes to the whisper's
// action (chat seed, map view, etc.). Dismiss × marks the whisper
// dismissed in Firestore so it doesn't re-appear.

import React from 'react';
import { Box, Typography, IconButton, useTheme, Fade } from '@mui/material';
import { Close, AutoAwesome } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useActiveWhisper } from '../../hooks/useActiveWhisper';
import { useT } from '../../i18n/useT';

interface Props {
  /** Optional callback when the user taps the banner's action.
   *  When the whisper's action is 'ask', this gets the prompt to
   *  send into chat. When 'view', the banner navigates internally
   *  and the callback isn't fired. */
  onAsk?: (prompt: string) => void;
}

export const WhisperBanner: React.FC<Props> = ({ onAsk }) => {
  const t = useT();
  const theme = useTheme();
  const navigate = useNavigate();
  const { whisper, dismiss } = useActiveWhisper();

  if (!whisper) return null;

  // Compose localized banner text from structured fields when the
  // server provided them; fall back to the server's English prose
  // for older docs. Same for the tap-to-ask prompt — composing it
  // client-side means the seeded chat message is in the user's
  // language, which also nudges the AI to reply in kind.
  const displayText =
    whisper.title && whisper.distance_rounded_m != null
      ? t('{title} is about {m}m from here — {hint}', {
          title: whisper.title,
          m: whisper.distance_rounded_m,
          hint: whisper.hint || t("worth a look if you've got a minute"),
        })
      : whisper.prose;

  const askPrompt = whisper.title
    ? t('Tell me about {title}', { title: whisper.title })
    : whisper.action_payload;

  const handleTap = () => {
    if (whisper.action === 'view' && whisper.action_payload) {
      navigate(whisper.action_payload);
    } else if (whisper.action === 'ask' && askPrompt && onAsk) {
      onAsk(askPrompt);
      dismiss();
    }
  };

  const tappable =
    !!whisper.action &&
    (whisper.action === 'ask' ? !!askPrompt : !!whisper.action_payload);

  return (
    <Fade in mountOnEnter unmountOnExit timeout={300}>
      <Box
        sx={{
          mx: 1,
          mb: 1,
          // Floating-bar look: rounded, frosted, very small. Should
          // feel like an aside, not a notification.
          borderRadius: 999,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: theme.palette.divider,
          backdropFilter: 'saturate(160%) blur(8px)',
          px: 1.5,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: tappable ? 'pointer' : 'default',
          '&:hover': tappable ? { bgcolor: 'action.hover' } : undefined,
          transition: 'background-color 120ms ease-out',
        }}
        onClick={tappable ? handleTap : undefined}
        role={tappable ? 'button' : undefined}
        aria-label={tappable ? t('Open whisper') : undefined}
      >
        <AutoAwesome
          sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }}
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            color: 'text.primary',
            lineHeight: 1.35,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {displayText}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          aria-label={t('Dismiss')}
          sx={{ p: 0.25, color: 'text.secondary' }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Fade>
  );
};
